import { IkeCaptureType, IkeToken } from '@ikegps/core-api';
import {
	type CollectionModel,
	CollectionModelFieldAnchor,
	CollectionModelFieldHeight,
	CollectionModelFieldImage,
	CollectionModelFieldLocation,
	CollectionModelFieldMidSpan,
	CollectionModelFieldNumber,
	CollectionModelFieldOrientation,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
} from '@ikegps/core-shared';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { CaptureThumbnail } from '../capture.thumbnail/capture.thumbnail';
import { CSS, Style, THUMBNAIL_SIZE } from './capture.bar.style';

addStyles(Style);

export const CaptureBar = {
	view(collection?: CollectionModel): MithrilVNode {
		if (collection == null) {
			return m('div', { className: CSS.container });
		}

		const seen: { [captureId: string]: boolean } = {};
		// We use these three arrays here to ensure a consistent order of photos when rendering
		const ikePhotos: MithrilVNode[] = [];
		const tagPhotos: MithrilVNode[] = [];
		const otherPhotos: MithrilVNode[] = [];

		collection.forEachField((field) => {
			let isIkePhoto = false;
			let photoBucket: MithrilVNode[];
			if (CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
				photoBucket = ikePhotos;
				isIkePhoto = true;
			} else if (CollectionModelFieldImage.isImageField(field)) {
				photoBucket = tagPhotos;
			} else if (
				Services.Token.has(IkeToken.FEATURE_DISPLAY_ALL_PHOTOS) &&
				(CollectionModelFieldHeight.isHeightField(field) ||
					CollectionModelFieldAnchor.isAnchorField(field) ||
					CollectionModelFieldNumber.isNumberField(field) ||
					CollectionModelFieldOrientation.isOrientationField(field) ||
					CollectionModelFieldLocation.isLocationField(field) ||
					CollectionModelFieldVector.isVectorField(field))
			) {
				photoBucket = otherPhotos;
			} else {
				return;
			}

			// A MidSpan field owns all of its captures like any other photo field; the ground reference shot
			// is only singled out for its badge — it is the evidence behind the derived base.
			const midSpan = CollectionModelFieldMidSpan.isMidSpanField(field) ? field : null;
			const groundCapture = midSpan?.groundCapture ?? null;
			const captures = field.getOwnedCaptures() || [];
			captures.sort((a, b) => {
				// `collectedAt` is optional; an undated capture sorts to the end of this newest-first order.
				const aIso = a.collectedAt?.iso ?? '';
				const bIso = b.collectedAt?.iso ?? '';
				if (aIso < bIso) {
					return 1;
				}
				if (aIso > bIso) {
					return -1;
				}
				return 0;
			});
			for (const capture of captures) {
				if (!seen[capture.objectId]) {
					const isGroundReference = capture.objectId === groundCapture?.objectId;
					const name = isGroundReference
						? `${field.schema.displayName} (${Translate(T.MID_SPAN_GROUND_REFERENCE)})`
						: field.schema.displayName;
					const title = `${name} - ${Services.DateTime.formatDateTime(capture.collectedAt?.iso)}`;
					seen[capture.objectId] = true;
					const thumbnail = CaptureThumbnail.view(collection, capture, {
						truesize: capture.type === IkeCaptureType.TRUE_SIZE,
						title,
						selected: CurrentState.inspectedCaptureId === capture.objectId,
						action: () =>
							CurrentState.setInspectedCollection(CurrentState.inspectedCollectionRevision, capture.objectId),
						// The ground shot carries no measurements of its own, so it gets no measurable badge.
						icon: isIkePhoto && !isGroundReference,
						size: THUMBNAIL_SIZE,
					});
					photoBucket.push(thumbnail);
				}
			}
		});

		return m('div', { className: CSS.container }, ikePhotos.concat(tagPhotos, otherPhotos));
	},
};
